import { FlatThemeConfig, ThemeConfig } from './theme';

// Core Types
export interface FlowNode<T extends NodeData = NodeData> {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: T;
  connections: Connection[];
}

export type NodeType =
  | 'flow_step'
  | 'text_input'
  | 'textarea'
  | 'email_input'
  | 'number_input'
  | 'phone_input'
  | 'single_choice'
  | 'multiple_choice'
  | 'rating'
  | 'consent'
  | 'date_picker'
  | 'file_upload'
  | 'conditional'
  | 'calculation'
  | 'thank_you'
  | 'landing_page'
  | 'end_page';

export interface NodeData {
  label: string;
  description?: string;
  required?: boolean;
  validation?: ValidationRule[];
  options?: ChoiceOption[];
  settings?: Record<string, any>;
  logic?: LogicRule[];
  loop?: LoopConfig;
  questions?: Question[];
  coverImageUrl?: string; // Image URL for landing_page and end_page
  markdownContent?: string; // Markdown text for landing_page and
}

export interface Connection {
  targetNodeId: string;
  condition?: ConditionRule;
}

export interface ConditionRule {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
  value: any;
}

export interface ValidationRule {
  type:
    | 'required'
    | 'min_length'
    | 'max_length'
    | 'min'
    | 'max'
    | 'email'
    | 'url'
    | 'regex';
  value?: any;
  message?: string;
  min?: number;
  max?: number;
  pattern?: string;
  [key: string]: any;
}

export interface ChoiceOption {
  id: string;
  label: string;
  value: string;
  allowOther?: boolean;
  otherPlaceholder?: string;
}

export interface RatingOption {
  id: string;
  score: number;
  label?: string;
  value: string;
}

export const DEFAULT_RATING_SCALE = 5;

const DEFAULT_RATING_OPTION_TEMPLATES: Omit<RatingOption, 'id'>[] = [
  { score: 1, value: 'very_dissatisfied' },
  { score: 2, value: 'dissatisfied' },
  { score: 3, value: 'neutral' },
  { score: 4, value: 'satisfied' },
  { score: 5, value: 'very_satisfied' },
];

export function createDefaultRatingOptions(): RatingOption[] {
  const baseId = Date.now();
  return DEFAULT_RATING_OPTION_TEMPLATES.map((option, index) => ({
    ...option,
    id: `rating_${baseId}_${index}`,
  }));
}

export function resolveRatingOptions(
  options?: Array<{ id: string; score?: number; label?: string; value: string }>,
  validation?: ValidationRule[]
): RatingOption[] {
  if (options && options.length > 0) {
    return options
      .map((option, index) => ({
        id: option.id,
        score: option.score ?? index + 1,
        label: option.label,
        value: option.value,
      }))
      .sort((a, b) => a.score - b.score);
  }

  const maxRule = validation?.find(
    (rule) => rule.type === 'max' || rule.type === 'max_length'
  );
  const scale = maxRule?.value ?? DEFAULT_RATING_SCALE;

  return Array.from({ length: scale }, (_, index) => {
    const score = index + 1;
    return {
      id: `fallback_${score}`,
      score,
      label: String(score),
      value: String(score),
    };
  });
}

export function getSelectedRatingScore(
  value: string | number | undefined | null,
  options: RatingOption[]
): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  const stringValue = String(value);
  const byValue = options.find((option) => option.value === stringValue);
  if (byValue) {
    return byValue.score;
  }

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue)) {
    const byScore = options.find((option) => option.score === numericValue);
    if (byScore) {
      return byScore.score;
    }
  }

  return undefined;
}

// Step and Field types for Flow Builder
export interface Step {
  id: string;
  title: string;
  description?: string;
  fields: Field[];
  logic?: LogicRule[];
  loop?: {
    enabled: boolean;
    sourceFieldId?: string;
    minCount?: number;
    maxCount?: number;
    labelTemplate?: string;
  };
}

export interface ConsentFieldConfig {
  identifierField?: string;
  purposeId?: string;
}

export interface BaseField {
  id: string;
  type: NodeType;
  fieldKey: string;
  description?: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: {
    id: string;
    label?: string;
    value: string;
    score?: number;
    [key: string]: any;
  }[];
  settings?: Record<string, any>;
  binding?: {
    type: 'query_param';
    name: string;
    required?: boolean;
    fallback?: string;
  };
  fileTypes?: string[];
  consentConfig?: ConsentFieldConfig;
}

export interface Field extends BaseField {
  validation?: ValidationRule;
}

// API/metadata-specific fields
export interface Question extends BaseField {
  validation?: ValidationRule[]; // Can override for API-specific validation format
}

export interface Condition {
  id: string;
  fieldId: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'greater_than'
    | 'less_than'
    | 'contains'
    | 'not_empty'
    | 'is_empty';
  value: string | number;
  logicOperator?: 'AND' | 'OR';
}

export interface LogicActions {
  jumpToStep?: string;
  showFields?: string[];
  hideFields?: string[];
  requireFields?: string[];
  hideStep?: string;
  showStep?: string;
}

export interface LogicRule {
  id: string;
  stepId: string;
  conditions: Condition[];
  actions: LogicActions;
}

export interface LoopConfig {
  enabled: boolean;
  sourceFieldId: string;
}

// Flow Definition
export interface Flow {
  id: string;
  title: string;
  description?: string;
  startDate?: Date;
  endDate?: Date;
  maximumRegister?: number;
  nodes: FlowNode[];
  settings: FlowSettings;
  theme: ThemeConfig;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  status: 'draft' | 'published' | 'unpublished' | 'archived';
  draftContent?: {
    nodes: FlowNode[];
    settings: FlowSettings;
    theme: FlatThemeConfig;
  };
  publishedContent?: {
    nodes: FlowNode[];
    settings: FlowSettings;
    theme: FlatThemeConfig;
  };
  /** Number of submitted responses for this flow */
  responsesCount?: number;
}

export interface FormStep {
  id: string;
  node: FlowNode;
  loopIndex?: number;
  isVisible: boolean;
}

export interface FlowSettings {
  allowMultipleSubmissions: boolean;
  showProgressBar: boolean;
  requireAuth: boolean;
  collectAnalytics: boolean;
  redirectUrl?: string;
  webhooks?: Webhook[];
  errorMessages?: {
    formClosed?: {
      message?: string;
      details?: string;
    };
    registrationFull?: {
      message?: string;
      details?: string;
    };
    notInPeriod?: {
      message?: string;
      details?: string;
    };
    checkFailed?: {
      message?: string;
      details?: string;
    };
  };
}
// Multi-tenant Types
export interface Tenant {
  id: string;
  name: string;
  domain: string;
  subdomain: string;
  branding: TenantBranding;
  settings: TenantSettings;
  plan: 'free' | 'pro' | 'enterprise';
  createdAt: Date;
}

export interface TenantBranding {
  logo?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily?: string;
  customCSS?: string;
}

export interface TenantSettings {
  allowEmbedding: boolean;
  allowWhiteLabel: boolean;
  customDomain: boolean;
  ssoEnabled: boolean;
  apiAccess: boolean;
  maxForms: number;
  maxSubmissions: number;
}

// User & Auth Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string;
  permissions: Permission[];
  createdAt: Date;
  lastLoginAt?: Date;
}

export type UserRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Permission {
  resource: string;
  actions: string[];
}

// Form Response Types
export interface FormResponse {
  id?: string;
  flowId: string;
  tenantId?: string;
  responses: ResponseData[];
  metadata: ResponseMetadata;
  submittedAt?: Date;
}

export interface ResponseData {
  nodeId: string;
  value: FieldValue;
  type: NodeType;
  label?: string;
  fieldKey: string;
  questionId: string;
}

export interface ResponseMetadata {
  userAgent?: string;
  ipAddress?: string;
  referrer?: string;
  completionTime: number; // in seconds
  deviceType: 'desktop' | 'tablet' | 'mobile';
  startedAt?: string;
  submittedAt: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T = any> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Embed Types
export interface EmbedConfig {
  flowId: string;
  theme?: Partial<ThemeConfig>;
  width?: string;
  height?: string;
  autoResize?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  customCSS?: string;
}

export interface Event {
  eventId?: string;
  name: string;
  description: string;
  location: string;
  startDateTime: Date;
  endDateTime: Date;
  feedbackUrl: string;
  registrationUrl: string;
  createdAt: Date;
  updatedAt: Date;
  emailTemplateCodename?: string;
}

export type EventFlowType = 'feedback' | 'registration';

export interface BasicFlow {
  id: string;
  type: EventFlowType;
  title: string;
  publicUrl?: string;
}
export interface EventWithFlows extends Event {
  flows: BasicFlow[];
}

export interface Webhook {
  id: string;
  name: string;
  url: string;
  secret?: string;
  isActive?: boolean;
}

export interface FileMetadata {
  file: File;
  fieldName: string;
  id: string;
  questionId: string;
}

export type FieldValue =
  | string
  | number
  | boolean
  | string[] // for multi-select
  | FileMetadata[]
  | null;

export interface ConsentProvider {
  id: string;
  name: string;
  type: 'onetrust' | 'insider';
  api_key?: string;
  api_endpoint?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConsentProviderListResponse {
  providers: ConsentProvider[];
  total: number;
}

export interface ConsentPurpose {
  id: string;
  name: string;
  purposeId: string; // External ID (e.g., OneTrust purpose ID)
  description?: string;
}

export interface EmailTemplate {
  key: string;
  label: string;
}

/**
 * Reserved fieldKey values for person / event registration integrations
 * (Martech DC RegisterService, Salekit, CRM). Prefer these over label-slugs.
 */
export const RESERVED_PERSON_FIELD_KEYS = [
  'first_name',
  'last_name',
  'email',
  'mobile_number',
  'event_id',
] as const;

export type ReservedPersonFieldKey =
  (typeof RESERVED_PERSON_FIELD_KEYS)[number];

/** Keys required on event-registration flows before publish. */
export const REQUIRED_EVENT_REGISTRATION_FIELD_KEYS = [
  'first_name',
  'last_name',
  'email',
] as const;

export const PERSON_FIELD_PRESETS: ReadonlyArray<{
  key: ReservedPersonFieldKey;
  label: string;
  description: string;
  inputType: 'text_input' | 'email_input' | 'phone_input';
}> = [
  {
    key: 'first_name',
    label: 'First name',
    description: 'Maps to attendee first name',
    inputType: 'text_input',
  },
  {
    key: 'last_name',
    label: 'Last name',
    description: 'Maps to attendee last name',
    inputType: 'text_input',
  },
  {
    key: 'email',
    label: 'Email',
    description: 'Maps to attendee email',
    inputType: 'email_input',
  },
  {
    key: 'mobile_number',
    label: 'Mobile number',
    description: 'Maps to attendee phone',
    inputType: 'phone_input',
  },
  {
    key: 'event_id',
    label: 'Event ID',
    description: 'Usually bound to ?eventId= query param',
    inputType: 'text_input',
  },
];

export function isReservedFieldKey(
  fieldKey: string | null | undefined
): fieldKey is ReservedPersonFieldKey {
  if (!fieldKey) return false;
  return (RESERVED_PERSON_FIELD_KEYS as readonly string[]).includes(fieldKey);
}
